import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';

import { addPageData, queryUnpaidTuitionCostInLoanCurrencyRequest } from '../../actions';
import Button from '../../components/Button';
import CombinedTextField from '../../components/CombinedTextField';
import InputField from '../../components/InputField';
import PageLayout from '../../components/PageLayout';
import Select from '../../components/Select';
import analytics from '../../lib/analytics';
import useCampusCountryCurrency from '../../lib/hooks/useCampusCountryCurrency';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { periodSelector, unpaidTuitionCostSelector } from '../../reducers/selectors';

const UnpaidTuitionCostPage = ({
  unpaidTuitionCost,
  period,
  dispatchAddPageData,
  dispatchGetUnpaidTuitionCostInLoanCurrency,
}) => {
  const [error, setError] = useState(false);
  const [currency, loanCurrencies] = useCampusCountryCurrency();
  const [amount, setAmount] = useState(unpaidTuitionCost.schoolAmount);

  const handleSubmit = event => {
    event.preventDefault();

    if (!areAllFieldsFilledIn || isInvalid) return;

    analytics.sendEvent('Click - Next', 'unpaid-tuition-cost');

    const unpaidTuitionCostPayload = payloadFactory.createAddPageDataPayload('unpaidTuitionCost', {
      schoolCurrency: currency,
      schoolAmount: amount,
      loanCurrency: '',
      loanAmount: '',
    });
    dispatchAddPageData(unpaidTuitionCostPayload);
    dispatchGetUnpaidTuitionCostInLoanCurrency();
  };

  const changeState = (event, setter) => {
    const { value } = event.target;
    setter(value);
  };

  const areAllFieldsFilledIn = amount;

  const isInvalid = useMemo(() => {
    if (amount < 0) {
      setError('Entered amount cannot be negative');
      return true;
    }

    setError(null);
    return false;
  }, [amount]);

  const shouldShowError = areAllFieldsFilledIn && isInvalid;

  const renderError = () => {
    if (shouldShowError) {
      return <div className="row-spacer margin-top-bottom text-error">{error}</div>;
    }

    return null;
  };

  const inputErrorClass = shouldShowError ? 'has-error' : '';

  const helpText =
    'We can only give a loan towards your unpaid tuition cost as this loan will be paid directly to the school.';

  return (
    <PageLayout
      title="How much of the tuition cost is still unpaid for this period?"
      socialProofText={['unpaid-tuition']}
    >
      <form onSubmit={handleSubmit}>
        <p className="text-blue size-2 text-medium mb-4">{period.periodName}</p>
        <p className="mb-4">{helpText}</p>
        <div>
          <p className="text-field__input-header form-label mb-0">Unpaid tuition cost</p>
          <CombinedTextField>
            <Select
              id="tuition-cost-currency"
              data-testid="tuition-cost-currency"
              value={currency}
              onChange={() => {}}
              options={loanCurrencies}
            />

            <InputField
              type="number"
              id="tuition-cost-amount"
              placeholder="Enter amount"
              value={amount}
              onChange={e => changeState(e, setAmount)}
              className={inputErrorClass}
            />
          </CombinedTextField>
        </div>

        <div className="mt-12">
          <Button
            type="submit"
            className="niso-button"
            onClick={e => handleSubmit(e)}
            isDisabled={isInvalid}
          >
            Next
          </Button>
        </div>
        {renderError()}
      </form>
    </PageLayout>
  );
};

UnpaidTuitionCostPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchGetUnpaidTuitionCostInLoanCurrency: PropTypes.func.isRequired,
  unpaidTuitionCost: PropTypes.shape({
    schoolAmount: PropTypes.string.isRequired,
    schoolCurrency: PropTypes.string.isRequired,
    loanAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    loanCurrency: PropTypes.string.isRequired,
  }).isRequired,
  period: PropTypes.shape({
    periodName: PropTypes.string.isRequired,
    startDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    endDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    classStatus: PropTypes.string,
    periodOrder: PropTypes.number,
    currency: PropTypes.string,
    costOfTuition: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export const mapStateToProps = state => ({
  unpaidTuitionCost: unpaidTuitionCostSelector(state),
  period: periodSelector(state),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchGetUnpaidTuitionCostInLoanCurrency: queryUnpaidTuitionCostInLoanCurrencyRequest,
};

export { UnpaidTuitionCostPage };

export default connect(mapStateToProps, mapDispatchToProps)(UnpaidTuitionCostPage);
